import { ChangeDetectorRef, Component } from '@angular/core';

import { FieldType, FormlyFieldConfig } from '@ngx-formly/core';

import { SectionFieldType, CoreService, FIELD_TYPE } from 'dspx-core';

import { APP_DATA_KEYS, COLLAPSIBLE_SECTION_TIMING } from '../../constants/app-data.constants';

import { LoggedInUserRoleFlags } from '../../interfaces/logged-in-user-role-flags.interface';

import { AppDataService } from '../../services/app-data.service';

@Component({
  selector: 'dspx-card-section-collapsible',
  templateUrl: './card-section-collapsible.component.html',
  styleUrl: './card-section-collapsible.component.scss'
})
export class CardSectionCollapsibleFieldType extends SectionFieldType {

  sectionBodyFields: FormlyFieldConfig[] = [];
  existsField: FormlyFieldConfig;
  notAssessedField: FormlyFieldConfig;
  existingData: any[] = [];
  isSectionMandatory: boolean = false;
  lastInspectionDate: any = null;
  canUseExistingData: boolean = false;

  getQuestionLayoutStyleClass = (question: FieldType): string => {
    return question.props.columnLayout === 'split'
      ? 'col-6 md:col-6'
      : (question.props.columnLayout ?? 'col-12 question');
  }

  constructor(
      protected coreService: CoreService, 
      private changeDetector: ChangeDetectorRef,
      private appDataService: AppDataService) {
      super();
    }

  ngOnInit() {
    this.getFields();
    this.field.props.totalQuestionCount = this.sectionBodyFields.length;
    this.field.props.isCollapsed = true;
    this.field.props.exists = true;
    this.field.props.notAssessed = false;
    this.field.props.answeredQuestionCount = this.getAnsweredCount(this.field) ?? 0;

    this.canUseExistingData = this.canUseExistingInspectionData();
    this.field.formControl?.valueChanges.subscribe(value => {
      this.trackFieldChanges(this.field);
    });

    this.field.fieldGroup.forEach(
      (question) => {
        if (this.isExistsQuestion(question.key.toString())) {
          this.field.props.exists = question.formControl.value;
        }

        if (this.isNotAssessedQuestion(question.key.toString())) {
          this.field.props.notAssessed = question.formControl.value;
        }
      });

      //load most recent previous data
      const previousInspectionData = this.field.props?.previousInspectionData;
      if (previousInspectionData && this.existingData.length === 0) {
        this.loadPreviousDataForSection(previousInspectionData);
    }
    this.lastInspectionDate = this.field.props?.lastInspectionDate;
  }

  private canUseExistingInspectionData(): boolean {
    return this.appDataService.getAppData<LoggedInUserRoleFlags>(APP_DATA_KEYS.loggedInUserRoleFlags)?.isPcrInspector ?? false;
  }

  trackFieldChanges(field: FormlyFieldConfig): void {
    if (field.model) {
      this.field.props.answeredQuestionCount = this.getAnsweredCount(field);
    }
  }

  clearSectionModel(field: FormlyFieldConfig): void {
    Object.keys(field.model).reduce((acc, key) => {
      if (!this.isHeaderQuestion(key)) {
        delete field.model[key];
      }
      return acc;
    }, {} as Record<string, any>);

    field.fieldGroup
      .filter(obj => !this.isHeaderQuestion(obj.key.toString()))
      .map(obj => {
        obj.formControl.setValue(undefined);
      });
  }

  getAnsweredCount(field: FormlyFieldConfig): number {
    if (field.model) {
      return Object.keys(field.model).filter(
        key => !this.isHeaderQuestion(key) && field.model[key] !== null && field.model[key] !== ''
      ).length;
    }
  }

  getFields(): void {
    this.existsField = this.field.fieldGroup.find(f => this.isExistsQuestion(f.key.toString()));
    this.notAssessedField = this.field.fieldGroup.find(f => this.isNotAssessedQuestion(f.key.toString()));
    this.sectionBodyFields = this.field.fieldGroup.filter(f => !this.isHeaderQuestion(f.key.toString()));

    if (this.notAssessedField?.props?.all?.length > 0) {
      this.notAssessedField.props.all.pop();
    }

    if (this.existsField?.props?.access === 'R') {
      this.isSectionMandatory = true;
    }

    this.existsField?.formControl?.valueChanges.subscribe(value => {
      this.updateSectionModel(this.existsField);
    });

    this.notAssessedField?.formControl?.valueChanges.subscribe(value => {
      this.updateSectionModel(this.notAssessedField);
    });
  }

  updateSectionModel(field: FormlyFieldConfig): void {
    if (this.isExistsQuestion(field.key.toString())) {
      this.field.props.exists = field.formControl.value;
      if (!this.field.props.exists) {
        this.field.props.isCollapsed = true;
        this.notAssessedField.props.disabled = true;
        if (this.notAssessedField.formControl.value) {
          this.notAssessedField.formControl.setValue(false);
        }
        this.clearSectionModel(this.field);
      }
      else {
        this.notAssessedField.props.disabled = false;
      }
    }
    if (this.isNotAssessedQuestion(field.key.toString())) {
      this.field.props.notAssessed = field.formControl.value;
      if (this.field.props.notAssessed) {
        this.field.props.isCollapsed = true;
        this.clearSectionModel(this.field);
      }
    }
  }

  setCollapsedStatus(field: FormlyFieldConfig): void {
    const currentSectionKey = field.key;
    field.props.isCollapsed = !field.props.isCollapsed;   
    field.parent?.parent?.parent?.fieldGroup?.forEach(tab => {
      tab.fieldGroup.forEach(page => {
        page.fieldGroup.forEach(section => {
          if (section.key !== currentSectionKey && !section.props.isCollapsed)
            section.props.isCollapsed = true;
        })
      })
    })
  }

  private loadPreviousDataForSection(previousInspectionData: any): void {
    const sectionKey = this.field.key?.toString();

    const sectionData = this.findSectionDataInPreviousInspection(previousInspectionData, sectionKey);

    if (!sectionData) {
      return;
    }

    this.existingData = [];

    this.sectionBodyFields.forEach(field => {
      const fieldKey = field.key.toString();

      if (this.isHeaderQuestion(fieldKey)) {
        return;
      }

      const previousValue = sectionData[fieldKey];

      if (previousValue !== undefined && previousValue !== null && previousValue !== '') {
        const tempField = { ...field, model: { [fieldKey]: previousValue } };
        const displayValue = this.coreService.getFieldValue(tempField) || previousValue?.toString() || '';
        const fieldLabel = field.props?.label || fieldKey;

        this.existingData.push({
          label: fieldLabel,
          value: displayValue,
          key: fieldKey,
          code: previousValue,
          fieldType: field.type
        });
      }
    });

  }

  private findSectionDataInPreviousInspection(previousInspectionData: any, sectionKey: string): any {
    const form = previousInspectionData?.form;
    if (!form?.tabs) {
      return null;
    }

    for (const tabKey in form.tabs) {
      const tab = form.tabs[tabKey];
      for (const pageKey in tab) {
        const page = tab[pageKey];
        if (page && page[sectionKey]) {
          return page[sectionKey];
        }
      }
    }

    return null;
  }  

  onUseExistingData(): void {
    const dataToApply = new Map<string, any>();

    this.sectionBodyFields.forEach(question => {
      const matchingData = this.existingData.find(data => data.key === question.key.toString());
      if (matchingData) {
        const options = question.props?.options;

        if (Array.isArray(options)) {
          const valueExists = options.some(opt => opt.code === matchingData.code);

          if (valueExists) {
            dataToApply.set(question.key.toString(), matchingData.code);
          } else {
            // If the GUID doesn't exist, set to null to trigger validation
            dataToApply.set(question.key.toString(), null);
          }
        } else {
          dataToApply.set(question.key.toString(), matchingData.code);
        }
      }
    });

    // Collapse and expand the section to force re-initialization
    this.field.props.isCollapsed = true;
    this.changeDetector.detectChanges();

    setTimeout(() => {
      // Apply all values to the model
      dataToApply.forEach((value, key) => {
        const question = this.sectionBodyFields.find(f => f.key.toString() === key);
        if (question) {
          question.model[key] = value;
          question.formControl?.setValue(value);
          question.formControl?.markAsTouched();
          question.formControl?.markAsDirty();
        }
      });

      this.field.props.isCollapsed = false;
      this.changeDetector.detectChanges();

      // Additional time for file upload components to initialize
      setTimeout(() => {
        this.field.formControl?.updateValueAndValidity();
        this.field.props.answeredQuestionCount = this.getAnsweredCount(this.field);
        this.changeDetector.detectChanges();
      }, COLLAPSIBLE_SECTION_TIMING.expandWaitMs);
    }, COLLAPSIBLE_SECTION_TIMING.collapseWaitMs);
  }

  getDisplayValue(value: any, type: any): string {
    if (!value && value !== 0) {
      return '';
    }

    const stringValue = String(value);

    if (type === FIELD_TYPE.file) {
      return stringValue.split(':')[0];
    }

    return stringValue;
  }

  getQuestionLabel(label: string): string {
    if (label.includes('Remaining Life')) {
      return 'Calculated ' + label;
    }
    return label;
  }

  isExistsQuestion = (questionCode: string): boolean => questionCode.endsWith('-exists');
  isNotAssessedQuestion = (questionCode: string): boolean => questionCode.endsWith('-notassessed');
  isHeaderQuestion = (questionCode: string): boolean => this.isExistsQuestion(questionCode) || this.isNotAssessedQuestion(questionCode);
}
